import {
  ApplicationFileSchema,
  ConfirmedVulnerabilityFindingStateSchema,
  DomainSourceSchema,
  ScopingFromRepoSchema,
  SignalStatusSchema,
} from '@terra/shared-schemas-orchestrator';
import { z } from 'zod';
import { PERMISSIONS, terraContract } from '@terra/shared-authorization';
import {
  BillingAiCapacitySchema,
  BillingAiCapacityTrendGranularitySchema,
  BillingAiCapacityTrendSchema,
  LicenseAiCapacitySchema,
  TenantLicenseUtilizationSchema,
} from '@terra/shared-contracts-cockpit';
import {
  CreateCredentialSchema,
  CreateCredentialsRequestSchema,
  CredentialsApiSchema,
  CredentialApiSchema,
  CredentialPreviewSchema,
  MfaProviderResponseSchema,
  GenerateCredentialMailboxResponseSchema,
  CredentialInboxMessagesResponseSchema,
} from './application-credentials/application-credentials.schema';
import {
  ApplicationRolesResponseSchema,
  ApplicationRoleResponseSchema,
  CreateApplicationRoleSchema,
  UpdateApplicationRoleSchema,
} from './application-roles/application-roles.schema';
import { CrawlerTypeSchema } from '@terra/shared-schemas-headless-browser';
import { APPLICATIONS_PATH_NAME } from './applications.constants';
import { DomainConfigReturnSchema } from './domain-config.schema';
import { isValidDomain } from './domain-validation';
import {
  ApplicationAssetsCountSchema,
  ApplicationCrawlerReturnSchema,
  ApplicationDashboardMetricsSchema,
  ApplicationDeleteSchema,
  ApplicationDiscoveryReturnSchema,
  TriggerRepositoryProfilingBodySchema,
  DomainEnumerationBodySchema,
  DomainEnumerationReturnSchema,
  ApplicationFilesSchema,
  ApplicationGuardrailCreateSchema,
  ApplicationGuardrailsReturnSchema,
  ApplicationGuardrailUpdateSchema,
  GuardrailValidationResponseSchema,
  ApplicationHealthMatrixSchema,
  ApplicationNoteCreateSchema,
  ApplicationNotesReturnSchema,
  ApplicationNoteUpdateSchema,
  NoteValidationResponseSchema,
  ApplicationRepositoriesReturnSchema,
  ApplicationDetailReturnSchema,
  ApplicationReturnSchema,
  ApplicationsBodySchema,
  ApplicationScopingBodySchema,
  ApplicationScopingReturnSchema,
  ApplicationsCountReturnSchema,
  ApplicationsIdSchema,
  ApplicationsReturnSchema,
  applicationUpdateBodySchema,
  DiscoveryTrendsSchema,
  SignalGenerationTrendsSchema,
  ApplicationUrlEditRestrictionSchema,
  ApplicationsMetricsQuerySchema,
  ApplicationsMetricsResponseSchema,
  PrTrendsResponseSchema,
  SignalsTrendsResponseSchema,
  TechnologiesWithCvesResponseSchema,
  ApplicationsSetupCompletenessDataBodySchema,
  ApplicationsSetupCompletenessDataResponseSchema,
  ApplicationProtoSchemaReturnSchema,
  ApplicationProtoSchemaAutoDiscoverReturnSchema,
  CrawlerSessionResponseSchema,
  CrawlerSessionStatsSchema,
  CrawlerLastFinishedByCredentialResponseSchema,
  BulkAbortCrawlerSessionsBodySchema,
  BulkAbortCrawlerSessionsResponseSchema,
  NotificationCenterMetricsSchema,
  NotificationCenterMetricsQuerySchema,
} from './applications.schema';
import {
  ApplicationInternalAccessResponseSchema,
  UpsertApplicationInternalAccessBodySchema,
  TestApplicationInternalAccessBodySchema,
  TestApplicationInternalAccessResponseSchema,
} from './application-internal-access/application-internal-access.schema';
import { ApplicationDiscoveryStatusResponseSchema } from './discovery-status/discovery-status.sse';
import {
  DynamicApplicationScanAutomationResponseSchema,
  StopDynamicApplicationScanResponseSchema,
  TriggerDynamicApplicationScanResponseSchema,
} from './dynamic-application-scan/dynamic-application-scan.schema';
import { assetMergeContractRoutes } from './asset-merge/asset-merge.contract';
import { DirectoryScanContractRoutes } from './directory-scan/directory-scan.contract';
import { ApplicationInteractionsContractRoutes } from './application-interactions/application-interactions.contract';
import {
  GetOnboardingTimelineResponseSchema,
  GetOnboardingTimelinesBodySchema,
  GetOnboardingTimelinesResponseSchema,
} from './onboarding-timeline/onboarding-timeline.schema';
import { adaptiveScanningContractRoutes } from './adaptive-scanning/adaptive-scanning.contract';
import { scanEffortContractRoutes } from './scan-effort/scan-effort.contract';
import { fullRescanContractRoutes } from './full-rescan/full-rescan.contract';
import { batchParametrizationControlsContractRoutes } from './batch-parametrization/batch-parametrization-controls.contract';
import { loginBehaviorContractRoutes } from './login-behavior/login-behavior.contract';
import { activityTrendContractRoutes } from './activity-trend/activity-trend.contract';

export const ApplicationsContract = terraContract
  .prefix(`/${APPLICATIONS_PATH_NAME}`)
  .router({
    getAllApplications: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/',
        summary: 'List all applications',
        description:
          'Retrieves applications for the authenticated tenant. By default returns web applications only; pass includeAllAppTypes=true or applicationType=all to include AI applications.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          query: z
            .object({
              includeAllAppTypes: z.coerce.boolean().optional(),
              applicationType: z
                .enum(['all', 'web', 'agent', 'mcp'])
                .optional(),
            })
            .optional(),
        }),
      )
      .output(ApplicationsReturnSchema),
    getApplication: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}',
        summary: 'Get application details',
        description:
          'Retrieves complete details for a single application including configuration, connected repositories, and testing settings.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ id: z.string() }) }))
      .output(ApplicationDetailReturnSchema),
    saveApplication: terraContract
      .meta({
        permissions: [PERMISSIONS.CREATE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/',
        summary: 'Create application',
        description:
          'Creates a new application for security testing. Supports connecting to GitHub/GitLab repositories and uploading OpenAPI/Swagger specifications for API discovery.',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: ApplicationsBodySchema }))
      .output(ApplicationReturnSchema),
    createDraftApplication: terraContract
      .meta({
        permissions: [PERMISSIONS.CREATE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/draft',
        summary: 'Create application as draft',
        description:
          'Creates a draft application without triggering discovery, crawling, or other automated testing pipelines.',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: ApplicationsBodySchema }))
      .output(ApplicationReturnSchema),
    activateApplication: terraContract
      .meta({
        permissions: [
          PERMISSIONS.CREATE_APPLICATIONS.name,
          PERMISSIONS.MANAGE_APPLICATIONS.name,
        ],
      })
      .route({
        method: 'POST',
        path: '/{id}/activate',
        summary: 'Activate draft application',
        description:
          'Transitions a draft application to active and runs deferred onboarding side effects such as repository scoping.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: ApplicationsIdSchema }))
      .output(ApplicationReturnSchema),
    updateApplication: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'PUT',
        path: '/{id}',
        summary: 'Update application',
        description:
          'Updates application settings including name, URL, environment, domains, and testing configuration options.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema,
          body: applicationUpdateBodySchema,
        }),
      )
      .output(ApplicationReturnSchema),
    deleteApplication: terraContract
      .meta({
        permissions: [PERMISSIONS.DELETE_APPLICATIONS.name],
      })
      .route({
        method: 'DELETE',
        path: '/{id}',
        summary: 'Delete application',
        description:
          'Permanently deletes an application and all associated data including findings, signals, endpoints, and test results. This action cannot be undone.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: ApplicationsIdSchema }))
      .output(ApplicationDeleteSchema),
    updateApplicationFiles: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'PUT',
        path: '/{id}/files',
        summary: 'Upload application files',
        description:
          'Upload files to the application such as OpenAPI/Swagger specs, documentation, or penetration testing reports. Supported file types include JSON, YAML, PDF, and common document formats.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema,
          body: ApplicationFilesSchema,
        }),
      )
      .output(ApplicationReturnSchema),
    getApplicationFiles: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/files',
        summary: 'List application files',
        description:
          'Retrieves metadata for all files uploaded to the application. Use this to display file lists and manage uploaded documentation.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: ApplicationsIdSchema }))
      .output(
        z
          .object({
            files: z
              .array(
                ApplicationFileSchema.pick({
                  id: true,
                  name: true,
                  size: true,
                  type: true,
                  mimeType: true,
                  createdAt: true,
                }),
              )
              .describe('List of uploaded files'),
          })
          .describe('Application files response'),
      ),
    getApplicationFileDownloadUrl: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/files/{fileId}/download-url',
        summary: 'Get application file download URL',
        description:
          'Retrieves a temporary presigned URL to download a specific application file.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema.extend({
            fileId: z
              .string()
              .describe('Unique identifier of the file to download'),
          }),
        }),
      )
      .output(
        z.object({
          downloadUrl: z.url().describe('Presigned download URL for the file'),
        }),
      ),
    deleteApplicationFile: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'DELETE',
        path: '/{id}/files/{fileId}',
        summary: 'Delete application file',
        description: 'Removes a specific file from the application.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema.extend({
            fileId: z
              .string()
              .describe('Unique identifier of the file to delete'),
          }),
        }),
      )
      .output(ApplicationDeleteSchema),
    getApplicationProtoSchema: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/proto-schema',
        summary: 'Get compiled proto schema bundle',
        description:
          'Returns services, methods, and compile warnings derived from the uploaded proto schema zip.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: ApplicationsIdSchema }))
      .output(ApplicationProtoSchemaReturnSchema),
    autoDiscoverApplicationProtoSchema: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/proto-schema/auto-discover',
        summary: 'Auto-discover proto schemas',
        description:
          'Triggers linked-repository proto collection for the application.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: ApplicationsIdSchema }))
      .output(ApplicationProtoSchemaAutoDiscoverReturnSchema),
    scopeApplicationByFiles: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/scope',
        summary: 'Define application scope from files',
        description:
          'Upload OpenAPI/Swagger specifications to define the API scope for testing. The system will parse endpoints and parameters from the specification.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema,
          body: ApplicationScopingBodySchema,
        }),
      )
      .output(ApplicationScopingReturnSchema),
    getApplicationScoping: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/scope',
        summary: 'Get application scope',
        description:
          'Retrieves the current testing scope configuration including discovered endpoints, excluded paths, and scoping rules.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: ApplicationsIdSchema }))
      .output(ApplicationScopingReturnSchema),
    getScopingFromRepo: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/repo-scoping',
        summary: 'Get scope from repository analysis',
        description:
          'Retrieves API endpoints and routes discovered by analyzing the connected source code repositories.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: ApplicationsIdSchema }))
      .output(ScopingFromRepoSchema.array()),
    scopeRepositoriesFromCode: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/scope/repositories',
        summary: 'Trigger code-based scoping',
        description:
          'Initiates static code analysis on connected repositories to discover API endpoints, routes, and parameters defined in the source code.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: ApplicationsIdSchema }))
      .output(ApplicationDiscoveryReturnSchema),
    triggerRepositoryProfiling: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/repositories/profiling',
        summary: 'Trigger repository profiling',
        description:
          'Starts repository profiling for every linked repository, or for one repository when repositoryId is set. Set force to replace an existing profile.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema,
          body: TriggerRepositoryProfilingBodySchema,
        }),
      )
      .output(ApplicationDiscoveryReturnSchema),
    runDiscovery: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/discovery',
        summary: 'Run asset discovery',
        description:
          'Triggers comprehensive asset discovery including API endpoint detection, web page crawling, and technology fingerprinting.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: ApplicationsIdSchema }))
      .output(ApplicationDiscoveryReturnSchema),
    domainEnumeration: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/domain-enumeration',
        summary: 'Enumerate subdomains for a given domain',
        description:
          'Uses passive subdomain enumeration (subfinder) to discover subdomains for the given domain. Can be called before application creation.',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: DomainEnumerationBodySchema }))
      .output(DomainEnumerationReturnSchema),
    getRepositories: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/repos',
        summary: 'List connected repositories',
        description:
          'Retrieves all source code repositories connected to this application for code analysis and change detection.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: ApplicationsIdSchema }))
      .output(ApplicationRepositoriesReturnSchema),
    getCredentials: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/credentials',
        summary: 'Get application credentials',
        description:
          'Retrieves authentication credentials configured for the application. These credentials are used during authenticated security testing. By default, disabled credentials are excluded.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema,
          query: z.object({
            decrypt: z
              .union([z.literal('true'), z.literal('false'), z.boolean()])
              .optional()
              .transform((val) => val === true || val === 'true')
              .describe(
                'If true, returns decrypted credential values. Requires elevated permissions.',
              ),
            includeDisabled: z
              .union([z.literal('true'), z.literal('false'), z.boolean()])
              .optional()
              .transform((val) => val === true || val === 'true')
              .describe(
                'If true, includes disabled credentials in the response. Defaults to false.',
              ),
          }),
        }),
      )
      .output(CredentialsApiSchema),
    saveCredentials: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/credentials',
        summary: 'Add credentials',
        description:
          'Adds new authentication credentials for the application. Supports various authentication types including username/password, API keys, OAuth tokens, and session cookies. Credentials are encrypted at rest.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema,
          body: CreateCredentialsRequestSchema,
        }),
      )
      .output(CredentialsApiSchema),
    saveCredential: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'PUT',
        path: '/{id}/credentials/save',
        summary: 'Create or update a single credential',
        description:
          'Creates a new credential or updates an existing one without affecting other credentials. If the credential has an id, it will be updated; otherwise a new credential will be created.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema,
          body: CreateCredentialSchema,
        }),
      )
      .output(CredentialApiSchema),
    verifyCredentials: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/credentials/verify',
        summary: 'Verify all credentials',
        description:
          'Tests all configured credentials by attempting to authenticate with the application. Returns success/failure status for each credential.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema,
          body: z
            .object({
              streamingIds: z
                .record(z.string(), z.string())
                .optional()
                .describe(
                  'Map of credential ID to WebSocket session ID for real-time verification updates',
                ),
            })
            .optional(),
        }),
      )
      .output(
        z
          .object({
            message: z.string().describe('Human-readable status message'),
            success: z
              .boolean()
              .describe('True if all credentials verified successfully'),
            results: z
              .array(
                z.object({
                  credentialId: z.string().describe('Credential identifier'),
                  success: z.boolean().describe('Verification result'),
                  error: z
                    .string()
                    .optional()
                    .describe('Error message if verification failed'),
                }),
              )
              .describe('Verification results for each credential'),
          })
          .describe('Credential verification response'),
      ),
    verifySingleCredential: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/credentials/{credentialId}/verify',
        summary: 'Verify single credential',
        description:
          'Tests a specific credential by attempting to authenticate. Supports real-time progress updates via WebSocket and AI-assisted login for complex authentication flows.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema.extend({
            credentialId: z
              .string()
              .describe('Credential identifier to verify'),
          }),
          body: z
            .object({
              streamingId: z
                .string()
                .optional()
                .describe(
                  'WebSocket session ID for real-time verification updates',
                ),
              shouldPreferUsingAIBrowser: z
                .boolean()
                .optional()
                .describe('Use AI-powered browser for complex login flows'),
            })
            .optional(),
        }),
      )
      .output(
        z
          .object({
            message: z.string().describe('Human-readable status message'),
            success: z.boolean().describe('True if verification succeeded'),
            error: z
              .string()
              .optional()
              .describe('Error details if verification failed'),
          })
          .describe('Single credential verification response'),
      ),
    abortCredentialVerification: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/credentials/{credentialId}/abort-verification',
        summary: 'Abort credential verification',
        description:
          'Aborts an in-progress credential verification. Releases the authentication lock, clears related caches, and sets verification status to FAILED.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema.extend({
            credentialId: z.string().describe('Credential identifier to abort'),
          }),
        }),
      )
      .output(
        z
          .object({
            success: z.boolean().describe('True if abort was successful'),
            message: z.string().describe('Human-readable status message'),
          })
          .describe('Abort credential verification response'),
      ),
    getMfaMessages: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/credentials/{credentialId}/mfa/{mfaConfigId}/messages',
        summary: 'Get MFA codes',
        description:
          'Retrieves current MFA/2FA codes or messages for credentials configured with multi-factor authentication providers like Twilio or email-based OTP.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema.extend({
            credentialId: z.string().describe('Credential identifier'),
            mfaConfigId: z.string().describe('MFA configuration identifier'),
          }),
          query: z
            .object({
              untruncated: z.coerce.boolean().optional(),
            })
            .optional(),
        }),
      )
      .output(MfaProviderResponseSchema),
    getCredentialInboxMessages: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/credentials/{credentialId}/inbox/messages',
        summary: 'Get credential inbox messages',
        description:
          'Retrieves emails from the credential mailbox (EMAIL MFA address or generated mailbox).',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema.extend({
            credentialId: z.string().describe('Credential identifier'),
          }),
        }),
      )
      .output(CredentialInboxMessagesResponseSchema),
    ingestMfaWebhook: terraContract
      .meta({ public: true })
      .route({
        method: 'POST',
        path: '/{id}/credentials/{credentialId}/mfa/{mfaConfigId}/webhook',
        summary: 'Ingest MFA webhook message',
        description:
          'Public endpoint for customers to push MFA codes/messages into Terra. Payload must be signed with per-credential secret.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema.extend({
            credentialId: z.string().describe('Credential identifier'),
            mfaConfigId: z.string().describe('MFA configuration identifier'),
          }),
          body: z.unknown().optional().describe('Inbound MFA webhook payload'),
        }),
      )
      .output(z.object({ success: z.literal(true) }))
      .errors({
        BAD_REQUEST: {
          status: 400,
          data: z.object({
            success: z.literal(false),
            message: z.string(),
          }),
        },
        UNAUTHORIZED: {
          status: 401,
          data: z.object({
            success: z.literal(false),
            message: z.string(),
          }),
        },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({
            success: z.literal(false),
            message: z.string(),
          }),
        },
      }),
    getMfaWebhookConfig: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/credentials/{credentialId}/mfa/{mfaConfigId}/webhook/config',
        summary: 'Get MFA webhook configuration',
        description:
          'Returns the inbound webhook URL path and signing headers/secret for credentials configured with webhook MFA.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema.extend({
            credentialId: z.string().describe('Credential identifier'),
            mfaConfigId: z.string().describe('MFA configuration identifier'),
          }),
        }),
      )
      .output(
        z.object({
          success: z.boolean(),
          endpointPath: z.string().describe('Relative webhook ingestion path'),
          signatureHeader: z.string(),
          timestampHeader: z.string(),
          deliveryIdHeader: z.string(),
          secret: z.string().describe('Webhook signing secret'),
          secretLast4: z.string().optional(),
          error: z.string().optional(),
        }),
      ),
    generateCredentialMailbox: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/mailbox/generate',
        summary: 'Generate a temporary mailbox address',
        description:
          'Generates a temporary email mailbox. Can be called before credential save.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema,
          body: z.object({}).optional(),
        }),
      )
      .output(GenerateCredentialMailboxResponseSchema),
    deleteCredential: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'DELETE',
        path: '/{id}/credentials/{credentialId}',
        summary: 'Delete credential',
        description:
          'Permanently removes a credential and all associated data including captured HTTP traffic (HAR entries) and discovered endpoints.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema.extend({
            credentialId: z
              .string()
              .describe('Credential identifier to delete'),
          }),
        }),
      )
      .output(
        z
          .object({
            success: z.boolean().describe('True if deletion succeeded'),
            message: z.string().describe('Status message'),
          })
          .describe('Credential deletion response'),
      )
      .errors({
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({
            error: z.string().describe('Error details'),
          }),
        },
      }),
    toggleCredentialStatus: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'PATCH',
        path: '/{id}/credentials/{credentialId}/status',
        summary: 'Enable or disable credential',
        description:
          'Toggles the enabled/disabled state of a credential. Disabled credentials are excluded from all operations including authentication, verification, and testing.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema.extend({
            credentialId: z.string().describe('Credential identifier'),
          }),
          body: z.object({
            isDisabled: z
              .boolean()
              .describe('True to disable the credential, false to enable it'),
          }),
        }),
      )
      .output(CredentialApiSchema),
    getCredentialsPreview: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/credentials/preview',
        summary: 'List credentials (preview)',
        description:
          'Retrieves a list of credentials showing only non-sensitive information (ID, username, role). Use this for displaying credential lists without exposing secrets.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: ApplicationsIdSchema }))
      .output(
        z
          .object({
            credentials: CredentialPreviewSchema.array().describe(
              'List of credential previews',
            ),
          })
          .describe('Credential previews response'),
      ),
    getRoles: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/roles',
        summary: 'Get application roles',
        description:
          'Retrieves all custom roles defined for the application. Roles can be linked to credentials and include descriptions that are injected into LLM prompts.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: ApplicationsIdSchema }))
      .output(ApplicationRolesResponseSchema),
    getRole: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/roles/{roleId}',
        summary: 'Get role details',
        description:
          'Retrieves details for a specific role including name, login URL, description, and AI analysis status.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema.extend({
            roleId: z.string().describe('Role identifier'),
          }),
        }),
      )
      .output(ApplicationRoleResponseSchema),
    createRole: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/roles',
        summary: 'Create role',
        description:
          'Creates a new custom role for the application. Roles can be reused across multiple credentials and include optional login URLs and descriptions.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema,
          body: CreateApplicationRoleSchema,
        }),
      )
      .output(ApplicationRoleResponseSchema),
    updateRole: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'PUT',
        path: '/{id}/roles/{roleId}',
        summary: 'Update role',
        description:
          'Updates role details including name, login URL, and description. Cannot update a role that is currently being analyzed.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema.extend({
            roleId: z.string().describe('Role identifier'),
          }),
          body: UpdateApplicationRoleSchema,
        }),
      )
      .output(ApplicationRoleResponseSchema),
    deleteRole: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'DELETE',
        path: '/{id}/roles/{roleId}',
        summary: 'Delete role',
        description:
          'Permanently removes a role. Cannot delete a role that is currently linked to any credentials.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema.extend({
            roleId: z.string().describe('Role identifier to delete'),
          }),
        }),
      )
      .output(
        z
          .object({
            success: z.boolean().describe('True if deletion succeeded'),
          })
          .describe('Role deletion response'),
      ),
    analyzeRole: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/roles/{roleId}/analyze',
        summary: 'Analyze role from source code',
        description:
          'Triggers AI-powered analysis of the role from source code integration. Analyzes codebase to extract role permissions, capabilities, and restrictions. Returns a job ID for tracking analysis progress.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema.extend({
            roleId: z.string().describe('Role identifier to analyze'),
          }),
        }),
      )
      .output(
        z
          .object({
            jobId: z.string().describe('Analysis job identifier'),
          })
          .describe('Role analysis trigger response'),
      )
      .errors({
        BAD_REQUEST: {
          status: 400,
          data: z.object({
            error: z.string().describe('Error message'),
          }),
        },
      }),
    runCrawler: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/crawler',
        summary: 'Start web crawler',
        description:
          'Initiates an AI-powered web crawler that navigates the application to discover pages, forms, and dynamic content. Optionally uses credentials for authenticated crawling.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema,
          body: z
            .object({
              credentialId: z
                .string()
                .nullable()
                .optional()
                .describe(
                  'Optional credential ID to use for authenticated crawling. Pass null for anonymous crawling, or not provided for all available credentials.',
                ),
            })
            .optional(),
        }),
      )
      .output(ApplicationCrawlerReturnSchema),
    getCrawlerStatus: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/crawler/status',
        summary: 'Get crawler status',
        description:
          'Returns the current status of the web crawler including whether it is running, session details, and progress information.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema,
          body: z.object({
            credentialId: z
              .string()
              .nullable()
              .describe(
                'Credential ID to check crawler status for. Pass null for anonymous crawler status check.',
              ),
          }),
        }),
      )
      .output(
        z
          .object({
            running: z
              .boolean()
              .describe('True if crawler is currently active'),
            metadata: z
              .object({
                startTime: z
                  .string()
                  .describe('ISO timestamp when crawler started'),
              })
              .optional()
              .describe('Active crawler session details'),
          })
          .describe('Crawler status response'),
      ),
    resetCrawlerExecution: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/crawler/reset',
        summary: 'Reset crawler',
        description:
          'Resets the crawler execution lock, useful if a previous crawl session was interrupted or stuck.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema,
          body: z.object({
            credentialId: z
              .string()
              .nullable()
              .describe('Credential ID to reset crawler for'),
          }),
        }),
      )
      .output(
        z
          .object({
            success: z.boolean().describe('True if reset succeeded'),
            message: z.string().describe('Status message'),
          })
          .describe('Crawler reset response'),
      ),
    getCrawlerSessions: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/crawler/sessions',
        summary: 'Get active crawler sessions',
        description:
          'Returns all active (running or stopping) crawler sessions for the application, ordered by createdAt descending.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema,
          body: z.object({
            credentialId: z
              .string()
              .nullable()
              .optional()
              .describe('Optional credential ID to filter sessions'),
          }),
        }),
      )
      .output(
        z
          .object({
            sessions: z.array(CrawlerSessionResponseSchema),
            stats: z.record(z.string(), CrawlerSessionStatsSchema),
          })
          .describe(
            'Active crawler sessions with stats, ordered by createdAt descending',
          ),
      ),
    getCrawlerLastFinishedByCredential: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/crawler/last-finished-by-credential',
        summary: 'Get last finished crawl time per credential',
        description:
          'Returns the newest finished crawler session timestamp and crawler type for each credential on the application, including anonymous (null credential).',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: ApplicationsIdSchema }))
      .output(CrawlerLastFinishedByCredentialResponseSchema),
    getOrCreateManualCrawlerSession: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/crawler/manual-session',
        summary: 'Create manual crawler session',
        description:
          'Creates a new manual crawler session. Call when starting manual tracking in the proxy so pages and HAR entries are attributed to this session. Complete the session when stopping via completeManualCrawlerSession.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema,
          body: z.object({
            credentialId: z
              .string()
              .nullable()
              .describe(
                'Credential ID for the manual tracking session, or null for anonymous tracking',
              ),
          }),
        }),
      )
      .output(
        z
          .object({
            crawlerSessionId: z.string().describe('Manual crawler session ID'),
          })
          .describe('Manual crawler session'),
      ),
    completeManualCrawlerSession: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/crawler/sessions/{sessionId}/complete',
        summary: 'Complete manual crawler session',
        description:
          'Marks a manual crawler session as COMPLETED. Call when the user stops manual tracking so the session is closed.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema.extend({
            sessionId: z
              .string()
              .describe('Manual crawler session ID to complete'),
          }),
          body: z.object({}).optional(),
        }),
      )
      .output(
        z
          .object({
            success: z.boolean().describe('True if session was completed'),
          })
          .describe('Complete manual session response'),
      ),
    requestBatchParametrizationOnBrowserClosed: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/crawler/manual-session/browser-closed',
        summary: 'Flush batch parametrization after browser close',
        description:
          'Publishes MANUAL_TRACKING_BROWSER_CLOSED so orchestrator can run batch URL parametrization after a short grace period. Does not complete or stop the manual crawler session — tracking remains active.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema,
          body: z.object({}).optional(),
        }),
      )
      .output(
        z
          .object({
            success: z
              .boolean()
              .describe('True if the flush event was published'),
          })
          .describe('Browser-closed batch flush response'),
      ),
    heartbeatManualCrawlerSession: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/crawler/sessions/{sessionId}/heartbeat',
        summary: 'Send heartbeat for manual crawler session',
        description:
          'Updates lastHeartbeatAt for a running manual crawler session to prevent stale cleanup. If the session was already marked FAILED by the cleanup scheduler, it is revived back to RUNNING so tracking can continue seamlessly.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema.extend({
            sessionId: z
              .string()
              .describe('Manual crawler session ID for heartbeat'),
          }),
          body: z.object({}).optional(),
        }),
      )
      .output(
        z
          .object({
            success: z.boolean().describe('True if heartbeat was recorded'),
          })
          .describe('Heartbeat response'),
      ),
    abortCrawlerSession: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/crawler/abort',
        summary: 'Abort crawler session(s)',
        description:
          'Abort by crawlerSessionId (single session), credentialId (all RUNNING sessions for that credential), or abortAll (every automated crawl for the application). At least one must be provided.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema,
          body: z
            .object({
              crawlerSessionId: z.string().optional(),
              credentialId: z.string().nullable().optional(),
              abortAll: z.boolean().optional(),
            })
            .refine(
              (data) =>
                data.crawlerSessionId !== undefined ||
                data.credentialId !== undefined ||
                data.abortAll === true,
              {
                error:
                  'At least one of crawlerSessionId, credentialId, or abortAll must be provided',
              },
            ),
        }),
      )
      .output(
        z
          .object({
            success: z.boolean().describe('True if abort was requested'),
            message: z.string().describe('Status message'),
            abortedCount: z.number().describe('Number of sessions aborted'),
          })
          .describe('Abort crawler session response'),
      ),
    bulkAbortCrawlerSessions: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/crawler/bulk-abort',
        summary: 'Bulk abort running crawler sessions',
        description:
          'Abort all RUNNING crawler sessions, optionally filtered by application IDs and crawler types. Requires Terra tenant access. MANUAL sessions are never aborted.',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: BulkAbortCrawlerSessionsBodySchema }))
      .output(BulkAbortCrawlerSessionsResponseSchema),
    getCrawlerHistory: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/crawler/history',
        summary: 'Get crawler session history',
        description:
          'Returns historical crawler sessions with filtering and pagination support. Sessions are ordered by createdAt descending.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema,
          body: z.object({
            filters: z
              .object({
                crawlerType: CrawlerTypeSchema.optional(),
                crawlerTypes: z.array(CrawlerTypeSchema).optional(),
                trigger: z.enum(['MANUAL', 'CRAWLER']).optional(),
                credentialId: z.string().nullable().optional(),
                credentialIds: z.array(z.string().nullable()).optional(),
                fromDate: z.string().optional(),
                toDate: z.string().optional(),
              })
              .optional(),
            cursor: z.string().optional(),
            limit: z.number().optional(),
          }),
        }),
      )
      .output(
        z
          .object({
            sessions: z.array(
              CrawlerSessionResponseSchema.extend({
                stats: CrawlerSessionStatsSchema,
              }),
            ),
            nextCursor: z.string().optional(),
            hasMore: z.boolean(),
          })
          .describe(
            'Crawler session history with pagination, ordered by createdAt descending',
          ),
      ),
    ...DirectoryScanContractRoutes,
    ...ApplicationInteractionsContractRoutes,
    triggerSmokeTest: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/smoke-test/trigger',
        summary: 'Trigger smoke test',
        description:
          'Initiates a quick security smoke test that runs a subset of test cases against discovered endpoints to verify basic security controls.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema,
          body: z
            .object({
              maxTests: z
                .number()
                .optional()
                .describe('Maximum number of test cases to execute'),
            })
            .optional(),
        }),
      )
      .output(
        z
          .object({
            message: z.string().describe('Status message'),
            triggered: z
              .boolean()
              .describe('True when at least one smoke test was queued'),
            triggeredCount: z
              .number()
              .int()
              .nonnegative()
              .describe('Number of smoke tests queued'),
          })
          .describe('Smoke test trigger response'),
      ),
    getApplicationsCount: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/count',
        summary: 'Get applications count',
        description:
          'Returns the total number of applications for the authenticated tenant.',
        inputStructure: 'detailed',
      })
      .output(ApplicationsCountReturnSchema),
    getAssetsCountByApplicationId: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/assets/count',
        summary: 'Get asset counts',
        description:
          'Returns the count of discovered assets (API endpoints and web pages) for a specific application.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: ApplicationsIdSchema }))
      .output(ApplicationAssetsCountSchema),
    getUrlEditRestriction: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/url-edit-restriction',
        summary: 'Check URL edit restriction',
        description:
          'Checks if the application URL can be edited. URL editing is restricted when the application has existing signals or endpoints.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: ApplicationsIdSchema }))
      .output(ApplicationUrlEditRestrictionSchema),
    getApplicationsMetrics: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/metrics',
        summary: 'Get metrics for applications',
        description:
          'Returns per-application metrics mapped by application ID. Use detectionView for active vs all detections, include to select metric groups, and applicationIds/environment/businessUnitId for scope. When from and/or to are set, counters are limited to that window; omit them for current posture. Period-over-period comparison is done client-side by calling twice.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          query: ApplicationsMetricsQuerySchema.optional(),
        }),
      )
      .output(ApplicationsMetricsResponseSchema),
    getApplicationsSetupCompletenessData: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/setup-completeness-data',
        summary: 'Get setup completeness inputs for applications',
        description:
          'Returns batched setup inputs (git integrations, credentials, documentation counts, knowledge mappings, and network binding) for computing application settings completeness on the client.',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: ApplicationsSetupCompletenessDataBodySchema }))
      .output(ApplicationsSetupCompletenessDataResponseSchema),
    getAggregatedAssetsCount: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/assets/count',
        summary: 'Get total asset counts',
        description:
          'Returns aggregated asset counts across all applications for the tenant.',
        inputStructure: 'detailed',
      })
      .output(ApplicationAssetsCountSchema),
    getDashboardMetrics: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/metrics/dashboard',
        summary: 'Get dashboard KPIs',
        description:
          'Retrieves key performance indicators for the dashboard including total applications, active tests, signals, and security scores.',
        inputStructure: 'detailed',
      })
      .output(ApplicationDashboardMetricsSchema),
    getPrTrends: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/metrics/pr-trends',
        summary: 'Get PR trends (total and reachable PRs) grouped by date',
        description:
          'Returns time-series data for PR counts (total and reachable) grouped by date, filtered by time range, environment, and business unit.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          query: z.object({
            from: z
              .string()
              .optional()
              .describe('Start date for the metrics period (ISO string)'),
            to: z
              .string()
              .optional()
              .describe('End date for the metrics period (ISO string)'),
            environment: z
              .string()
              .optional()
              .describe('Filter by environment'),
            businessUnitId: z
              .string()
              .optional()
              .describe('Filter by business unit ID'),
            applicationId: z
              .string()
              .optional()
              .describe('Filter by application ID'),
          }),
        }),
      )
      .output(PrTrendsResponseSchema),
    getSignalsTrends: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/metrics/signals-trends',
        summary:
          'Get signals trends (signals detected over time) grouped by date',
        description:
          'Returns time-series data for signals counts grouped by date, filtered by time range, environment, and business unit.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          query: z.object({
            from: z
              .string()
              .optional()
              .describe('Start date for the metrics period (ISO string)'),
            to: z
              .string()
              .optional()
              .describe('End date for the metrics period (ISO string)'),
            environment: z
              .string()
              .optional()
              .describe('Filter by environment'),
            businessUnitId: z
              .string()
              .optional()
              .describe('Filter by business unit ID'),
            applicationId: z
              .string()
              .optional()
              .describe('Filter by application ID'),
            statuses: SignalStatusSchema.array()
              .optional()
              .describe('Filter by signal statuses'),
            confirmedVulnerabilityFindingState:
              ConfirmedVulnerabilityFindingStateSchema.array()
                .optional()
                .describe(
                  'Filter confirmed vulnerability signals by finding state',
                ),
          }),
        }),
      )
      .output(SignalsTrendsResponseSchema),
    getHealthMatrix: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/metrics/health-matrix',
        summary: 'Get health matrix',
        description:
          'Returns security health metrics for all applications including finding counts, signal distribution, and risk scores.',
        inputStructure: 'detailed',
      })
      .output(ApplicationHealthMatrixSchema.array()),
    getDiscoveryTrends: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/metrics/discovery-trends',
        summary: 'Get discovery trends',
        description:
          'Returns monthly aggregated discovery trends showing findings, endpoints, and web pages discovered over time.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          query: z
            .object({
              months: z
                .string()
                .optional()
                .default('6')
                .describe('Number of months to include in trends (default: 6)'),
            })
            .optional(),
        }),
      )
      .output(DiscoveryTrendsSchema.array()),
    getDiscoveryTrendsByApplicationId: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/metrics/discovery-trends/application/{id}',
        summary: 'Get discovery trends by application',
        description:
          'Returns monthly discovery trends for a specific application.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema,
          query: z
            .object({
              months: z
                .string()
                .optional()
                .default('6')
                .describe('Number of months to include (default: 6)'),
            })
            .optional(),
        }),
      )
      .output(DiscoveryTrendsSchema.array()),
    getSignalGenerationTrends: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/metrics/signal-generation-trends',
        summary: 'Get signal generation trends',
        description:
          'Returns monthly signal generation trends showing hot, cold, passed, and exploited signals over time.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          query: z
            .object({
              months: z
                .string()
                .optional()
                .default('6')
                .describe('Number of months to include (default: 6)'),
            })
            .optional(),
        }),
      )
      .output(SignalGenerationTrendsSchema.array()),
    getApplicationGuardrails: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/guardrails',
        summary: 'List guardrails',
        description:
          'Retrieves all security guardrails configured for the application. Guardrails define security policies and testing boundaries.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: ApplicationsIdSchema }))
      .output(ApplicationGuardrailsReturnSchema),
    createApplicationGuardrail: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/guardrails',
        summary: 'Create guardrail',
        description:
          'Creates a new security guardrail with custom rules and actions. Guardrails can warn or block certain testing activities.',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(
        z.object({
          params: ApplicationsIdSchema,
          body: ApplicationGuardrailCreateSchema,
        }),
      )
      .output(GuardrailValidationResponseSchema),
    updateApplicationGuardrail: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'PUT',
        path: '/{id}/guardrails/{guardrailId}',
        summary: 'Update guardrail',
        description: 'Updates an existing guardrail configuration.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema.extend({
            guardrailId: z.string().describe('Guardrail identifier'),
          }),
          body: ApplicationGuardrailUpdateSchema,
        }),
      )
      .output(GuardrailValidationResponseSchema),
    deleteApplicationGuardrail: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'DELETE',
        path: '/{id}/guardrails/{guardrailId}',
        summary: 'Delete guardrail',
        description: 'Permanently removes a guardrail from the application.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema.extend({
            guardrailId: z.string().describe('Guardrail identifier'),
          }),
        }),
      )
      .output(ApplicationDeleteSchema),
    getApplicationNotes: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/notes',
        summary: 'List notes',
        description:
          'Retrieves all notes and testing instructions attached to the application.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: ApplicationsIdSchema }))
      .output(ApplicationNotesReturnSchema),
    createApplicationNote: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/notes',
        summary: 'Create note',
        description:
          'Adds a new note or testing instruction to the application. Notes can contain security context and testing guidance.',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(
        z.object({
          params: ApplicationsIdSchema,
          body: ApplicationNoteCreateSchema,
        }),
      )
      .output(NoteValidationResponseSchema),
    updateApplicationNote: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'PUT',
        path: '/{id}/notes/{noteId}',
        summary: 'Update note',
        description: 'Updates an existing note.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema.extend({
            noteId: z.string().describe('Note identifier'),
          }),
          body: ApplicationNoteUpdateSchema,
        }),
      )
      .output(NoteValidationResponseSchema),
    deleteApplicationNote: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'DELETE',
        path: '/{id}/notes/{noteId}',
        summary: 'Delete note',
        description: 'Removes a note from the application.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema.extend({
            noteId: z.string().describe('Note identifier'),
          }),
        }),
      )
      .output(ApplicationDeleteSchema),
    getApplicationDomains: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/domains',
        summary: 'List domains',
        description:
          'Retrieves all domains configured for the application. Domains define the scope boundaries for testing.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: ApplicationsIdSchema }))
      .output(
        z
          .object({
            domains: DomainConfigReturnSchema.array().describe(
              'List of configured domains',
            ),
          })
          .describe('Application domains response'),
      ),
    addDomain: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/domains',
        summary: 'Add domain',
        description:
          'Adds a new domain to the application scope. Testing will include endpoints on this domain.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema,
          body: z.object({
            domain: z
              .string()
              .refine((value) => isValidDomain(value), {
                message: 'Invalid domain',
              })
              .describe('Domain name to add (e.g., api.example.com)'),
            source: DomainSourceSchema.describe(
              'How the domain was discovered',
            ),
          }),
        }),
      )
      .output(
        z
          .object({
            success: z.boolean().describe('True if domain was added'),
            message: z.string().describe('Status message'),
          })
          .describe('Add domain response'),
      ),
    removeDomain: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'DELETE',
        path: '/{id}/domains/{domain}',
        summary: 'Remove domain',
        description: 'Removes a domain from the application scope.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema.extend({
            domain: z.string().describe('Domain name to remove'),
          }),
        }),
      )
      .output(
        z
          .object({
            success: z.boolean().describe('True if removal succeeded'),
            message: z.string().describe('Status message'),
          })
          .describe('Remove domain response'),
      ),
    getApplicationInternalAccess: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/internal-access',
        summary: 'Get application internal access binding',
        description:
          'Returns the Twingate network binding for an internal application.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: ApplicationsIdSchema }))
      .output(ApplicationInternalAccessResponseSchema),
    upsertApplicationInternalAccess: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'PUT',
        path: '/{id}/internal-access',
        summary: 'Update application internal access binding',
        description:
          'Binds an internal application to a Twingate network for scoped routing.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema,
          body: UpsertApplicationInternalAccessBodySchema,
        }),
      )
      .output(ApplicationInternalAccessResponseSchema),
    testApplicationInternalAccess: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/internal-access/test',
        summary: 'Test application internal access',
        description:
          'Runs connectivity checks for the application Twingate network binding.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema,
          body: TestApplicationInternalAccessBodySchema,
        }),
      )
      .output(TestApplicationInternalAccessResponseSchema),
    getNotificationCenterMetrics: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/notification-center-metrics',
        summary: 'Get notification center metrics for an application',
        description:
          'Returns aggregated notification center metrics for an application since the provided last-visit timestamp.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationsIdSchema,
          query: NotificationCenterMetricsQuerySchema,
        }),
      )
      .output(NotificationCenterMetricsSchema),
    getDiscoveryStatus: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/discovery-status',
        summary: 'Get application discovery status',
        description:
          'Returns the backend-authoritative discovery status for the application: idle, discovering (an automated crawl is active or pending), or processing (a batch parametrization run is active).',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: ApplicationsIdSchema }))
      .output(ApplicationDiscoveryStatusResponseSchema)
      .errors({
        BAD_REQUEST: {
          status: 400,
          data: z.object({
            message: z.string().describe('Error message'),
          }),
        },
        NOT_FOUND: {
          status: 404,
          data: z.object({
            message: z.string().describe('Error message'),
          }),
        },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({
            message: z.string().describe('Error message'),
          }),
        },
      }),
    getOnboardingTimeline: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/onboarding-timeline',
        summary: 'Get post-activation onboarding timeline for an application',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: ApplicationsIdSchema }))
      .output(GetOnboardingTimelineResponseSchema)
      .errors({
        NOT_FOUND: {
          status: 404,
          data: z.object({ message: z.string() }),
        },
      }),
    getOnboardingTimelines: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/onboarding-timelines',
        summary:
          'Get post-activation onboarding timelines for multiple applications',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: GetOnboardingTimelinesBodySchema }))
      .output(GetOnboardingTimelinesResponseSchema),
    getTopTechnologiesCves: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/technologies/top-cves',
        summary: 'Get top technologies CVEs',
        description:
          'Retrieves top technologies found in code graphs that have both endpoints and CVEs, sorted by number of endpoints. Only technologies with both endpoints and CVEs are returned. If applicationId is provided, businessUnitId and environment filters are ignored. Otherwise, businessUnitId and environment should be used together.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          query: z.object({
            businessUnitId: z
              .string()
              .optional()
              .describe(
                'Filter by business unit ID (ignored if applicationId is provided, must be used with environment otherwise)',
              ),
            environment: z
              .string()
              .optional()
              .describe(
                'Filter by environment (ignored if applicationId is provided, must be used with businessUnitId otherwise)',
              ),
            applicationId: z
              .string()
              .optional()
              .describe(
                'Filter by application ID (if provided, businessUnitId and environment are ignored)',
              ),
          }),
        }),
      )
      .output(TechnologiesWithCvesResponseSchema),
    getLicenseUtilization: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/license/utilization',
        summary: 'Get license utilization for the current tenant',
        description:
          'Returns the license configuration and current usage breakdown (applications and assets) for the authenticated tenant.',
        inputStructure: 'detailed',
      })
      .output(TenantLicenseUtilizationSchema),
    getLicenseAiCapacity: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/license/ai-capacity',
        summary: 'Get AI Capacity units for the current tenant',
        description:
          'Returns purchased and Terra-native burned AI Capacity units, including a by-model breakdown, for the authenticated tenant.',
        inputStructure: 'detailed',
      })
      .output(LicenseAiCapacitySchema),
    getLicenseAiCapacityTiers: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/license/ai-capacity-tiers',
        summary: 'Get AI Capacity budgets by model tier for the current tenant',
        description:
          'Returns consumed and budgeted AI Capacity spend grouped by billing model tier (specialized frontier, heavyweight reasoning, production utility) for the authenticated tenant.',
        inputStructure: 'detailed',
      })
      .output(BillingAiCapacitySchema),
    getLicenseAiCapacityTrend: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/license/ai-capacity-trend',
        summary: 'Get AI Capacity spend trend over time for the current tenant',
        description:
          'Returns customer-facing AI Capacity spend bucketed over time (by day, week, or month) and split by billing model tier for the authenticated tenant.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          query: z
            .object({
              granularity: BillingAiCapacityTrendGranularitySchema.optional(),
            })
            .optional(),
        }),
      )
      .output(BillingAiCapacityTrendSchema),
    getDynamicApplicationScanAutomation: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/dynamic-application-scan/automation',
        summary: 'Get dynamic application scan automation state',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: ApplicationsIdSchema }))
      .output(DynamicApplicationScanAutomationResponseSchema),
    triggerDynamicApplicationScan: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/dynamic-application-scan/trigger',
        summary: 'Trigger a dynamic application scan now',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: ApplicationsIdSchema }))
      .output(TriggerDynamicApplicationScanResponseSchema),
    stopDynamicApplicationScan: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/dynamic-application-scan/stop',
        summary: 'Stop the active dynamic application scan',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: ApplicationsIdSchema }))
      .output(StopDynamicApplicationScanResponseSchema),
    ...adaptiveScanningContractRoutes,
    ...assetMergeContractRoutes,
    ...scanEffortContractRoutes,
    ...fullRescanContractRoutes,
    ...batchParametrizationControlsContractRoutes,
    ...loginBehaviorContractRoutes,
    ...activityTrendContractRoutes,
  });
